import { IPaymentJs, IxopayWidget, TIxopayOptions } from "@kma-core/w2-w2w-payment-checkout";
import { IxopayFormData } from "./types";
import Cookies from "js-cookie";
import { IXOPAY_INTEGRATION_KEY, NODE_ENV, PAYMENT_SERVER_URL, PROJECT_NAME } from "constants/env.const";
import { useAppData, useUserStore } from "providers/RootStoreProvider";
import { CSSProperties, useMemo, useState } from "react";
import { loadFromStorage } from "services/storage";
import { ampli, SubscriptionSubscribeClickedProperties } from "services/ampli";
import { getFieldsStyles, PaywallType } from "./utils";
import { AppRoutes } from "enums/routes.enum";
import { Box, CircularProgress, SxProps } from "@mui/material";
import { PaymentErrorModal } from "./PaymentErrorModal";
import { usePaymentResult } from "./hooks";

interface Props {
    data: IxopayFormData;
    source?: string;
    paywall: PaywallType;
    sx?: SxProps;
    customLabels?: TIxopayOptions["customLabels"];
    submitEventProps?: Omit<SubscriptionSubscribeClickedProperties, "success">;
    disableSubmit?: boolean;
}

export const IxopayForm = ({ data, source, paywall, sx, customLabels, submitEventProps, disableSubmit = false }: Props) => {
    const { user, userWebSession } = useUserStore();
    const { flowOuter, amplitudeUserId, ipInfo, abTestData } = useAppData();
    const { plan, declineQuery, successQuery } = data;
    const [inputStyles, setInputStyles] = useState<Record<"default" | "focus", CSSProperties>>();
    const [widgetKey, setWidgetKey] = useState(0);
    const [error, setError] = useState<string | undefined>();

    const abTestPaywall = abTestData?.abTestPaywall || userWebSession?.session?.abTestData?.abTestPaywall;
    const countryCode = ipInfo?.country || "US";

    const fbc = Cookies.get("_fbc");
    const fbp = Cookies.get("_fbp");
    const gclid = loadFromStorage("gclid") || null;

    const submitProps = submitEventProps ?? {
        type: abTestPaywall,
        product_id: plan?.planId || "",
        plan: plan?.name || "",
    };

    const { handleDecline, handleSuccess } = usePaymentResult({ submitProps, declineQuery, successQuery });

    const handleModalClose = () => setError(undefined);

    const settings: TIxopayOptions = useMemo(() => {
        return {
            integrationKey: IXOPAY_INTEGRATION_KEY,
            secureFlowUrl: `${window.location.origin}${AppRoutes.SECURE_FLOW_RESULT}`,
            disableSubmit,
            successCallback: handleSuccess,
            failureCallback: (errors) => {
                handleDecline(errors, (type) => {
                    setError(type);
                    setWidgetKey((prev) => prev + 1);
                });
            },
            completeCallback: (paymentJs: IPaymentJs) => {
                const { style, focusStyle } = getFieldsStyles(paymentJs, paywall);
                setInputStyles({ default: style, focus: focusStyle });
            },
            customLabels: {
                card_holder: {
                    placeholder: "Cardholder name",
                },
                expiration_date: {
                    placeholder: "MM/YY",
                },
                submit_title: "Continue",
                ...customLabels,
            },
            customSubmitLoader: (
                <CircularProgress
                    sx={{
                        position: "absolute",
                        top: "calc(50% - 10px)",
                        left: "calc(50% - 10px)",
                        height: "20px !important",
                        width: "20px !important",
                        color: "text.secondary",
                    }}
                />
            ),
        };
    }, [plan, user, disableSubmit]);

    if (!plan) return null;

    return (
        <Box
            sx={{
                "& #cc-exp-date, #cc-name, #cc-exp-date, #cc-name": inputStyles
                    ? {
                          ...inputStyles.default,
                          "&:focus": inputStyles?.focus,
                      }
                    : {},
                "& .ixopay-input-label": {
                    display: "none",
                },
                "& .ixopay-submit-button-loading": {
                    color: "transparent !important",
                },
                "& .ixopay-helper-text": {
                    color: "rgb(211, 47, 47) !important",
                    margin: "3px 14px 0px",
                    fontSize: "12px",
                    lineHeight: 1.25,
                },
                ...sx,
            }}
        >
            <IxopayWidget
                key={widgetKey}
                options={settings}
                paymentInfo={{
                    environment: NODE_ENV === "production" ? "production" : "development",
                    apiBaseUrl: PAYMENT_SERVER_URL,
                    projectName: PROJECT_NAME,
                    planFetchParams: {
                        planAmplitudeId: plan.amplitudeId,
                        isTest: false,
                        countryCode: "US",
                        currency: "USD",
                    },
                    userFetchParams: {
                        email: user?.email as string,
                        userAgent: window.navigator.userAgent,
                        isLegal: !flowOuter,
                        amplitudeSessionId: amplitudeUserId as string,
                        countryCode,
                        ip: ipInfo?.ip as string,
                        fbc,
                        fbp,
                        city: ipInfo?.city as string,
                        postalCode: ipInfo?.postal as string,
                        state: ipInfo?.state as string,
                        gclid,
                    },
                    ampliConfig: {
                        ampli,
                        // checkout_type: checkoutType || "popup",
                        source,
                    },
                }}
            />
            <PaymentErrorModal onClose={handleModalClose} onSubmit={handleModalClose} error={error} />
        </Box>
    );
};
